import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import AdminSidebar from '../components/AdminSidebar';
import AdminInterupsiForm from '../components/forms/AdminInterupsiForm';

const AdminIjinInterupsiPage = ({ isSidebarOpen, onSidebarToggle }) => {
  const { darkMode } = useContext(ThemeContext);
  const [showForm, setShowForm] = useState(true);
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: true, // Open by default since we're in a surat ijin page
    laporanOpen: false, 
    masterDataOpen: false
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || 'Admin User',
    position: user?.role || 'Administrator',
    avatarUrl
  };

  const handleCloseForm = () => {
    setShowForm(false);
    // Navigate back to admin home page
    window.history.back();
  };

  return (
    <PageContainer>
      <DashboardGrid>
        <SidebarContainer $isOpen={isSidebarOpen}>
          <SidebarOverlay $isOpen={isSidebarOpen} onClick={() => onSidebarToggle && onSidebarToggle()} />
          <SidebarContent $isOpen={isSidebarOpen}>
            <SidebarHeader>
              <h3>Menu Admin</h3>
              <CloseButton onClick={() => onSidebarToggle && onSidebarToggle()}>&times;</CloseButton>
            </SidebarHeader>
            <AdminSidebar 
              user={adminData}
              accordionState={accordionState} 
              setAccordionState={setAccordionState}
              onSidebarToggle={onSidebarToggle}
            />
          </SidebarContent>
        </SidebarContainer>
        
        <MainSection>
          <PageHeader>
            <HeaderContent>
              <div>
                <h1>Ijin Interupsi</h1>
                <Subtitle>Form pengajuan ijin interupsi untuk siswa</Subtitle>
              </div>
            </HeaderContent>
          </PageHeader>
          
          {showForm && (
            <ContentSection>
              <FormContent>
                <AdminInterupsiForm 
                  onClose={handleCloseForm}
                  readOnly={false}
                />
              </FormContent>
            </ContentSection>
          )}
        </MainSection>
      </DashboardGrid>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
  background-color: ${props => props.theme === 'dark' ? '#1a1a1a' : '#ffffff'};
  color: ${props => props.theme === 'dark' ? '#ffffff' : '#333333'};
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#2a2a2a' : '#ffffff'};
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem 2rem;
`;

const FormContent = styled.div`
  padding: 2rem;
  background-color: ${props => props.theme === 'dark' ? '#2a2a2a' : '#ffffff'};
  color: ${props => props.theme === 'dark' ? '#ffffff' : '#333333'};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-light);
  }

  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 1rem;
    background: #ffffff;
    color: #333333;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
    
    &:read-only {
      background: var(--background-light);
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SubmitButton = styled.button`
  background: #2b5da1;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #1a4c90;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(43, 93, 161, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SidebarContainer = styled.div`
  @media (max-width: 1100px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
  }
  
  @media (min-width: 1101px) {
    order: 1;
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    transition: opacity 0.3s ease;
  }
`;

const SidebarContent = styled.div`
  @media (max-width: 1100px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    background-color: var(--background);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(0);
    transition: transform 0.3s ease;
    overflow-y: auto;
    z-index: 1001;
  }
  
  @media (max-width: 576px) {
    width: 260px;
  }
`;

const SidebarHeader = styled.div`
  display: none;
  
  @media (max-width: 1100px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border);
    background-color: var(--primary);
    color: white;
    
    h3 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export default AdminIjinInterupsiPage;